/**
 * Wire protocol between browser client and game host. The same ServerMessage stream is produced
 * by the multiplayer server (over WebSocket, msgpack-encoded) and by the single-player Web Worker
 * (over postMessage, structured clone) so the client has exactly one code path.
 */
import type {
  AiDifficulty,
  Command,
  CommandResult,
  FactionId,
  GameMap,
  MapSize,
  PlayerView,
  ReplayLog,
  ScenarioId,
  Season,
  SessionConfig,
  TechId,
  TheatreId,
  ViewDelta,
} from '@friction/engine';

export const PROTOCOL_VERSION = 1;

/** Game speed 1..5 → milliseconds per tick (one in-game hour). */
export const SPEED_TICK_MS: Record<GameSpeed, number> = { 1: 1600, 2: 1000, 3: 600, 4: 300, 5: 120 };
export type GameSpeed = 1 | 2 | 3 | 4 | 5;

// ---- lobby ------------------------------------------------------------------------------------

export interface RoomSettings {
  name: string;
  mapSize: MapSize;
  /** 0 = random seed chosen at start. */
  seed: number;
  dayLimit: number;
  vpVictoryShare: number;
  speed: GameSpeed;
  startingTechs: TechId[];
  /** Campaign season; 'random' = drawn from the match seed. */
  season: Season | 'random';
  /**
   * Scenario preset (engine `SCENARIOS`), or null for a free setup. A scenario fixes the theatre
   * size, season, day limit, victory share and starting techs (the fields above follow it) and
   * the slot structure: one slot per side of the scenario — slot 0 is the briefed side, every
   * nation fights for itself — and the rest closed. The host still decides who plays which side,
   * the AI's quality per slot, the seed and the speed.
   */
  scenario: ScenarioId | null;
  /**
   * Real-world theatre (engine `THEATRES`) for a free setup, or null for a generated map. A
   * theatre fixes the map size and the slot structure — one slot per nation of the theatre (slot
   * i plays nation i), every nation in play (open sides go to the computer), the rest closed —
   * while teams, season and terms stay the host's. With a scenario it is the scenario's theatre.
   */
  theatre: TheatreId | null;
}

export type SlotController = 'open' | 'closed' | 'human' | 'ai';

export interface RoomSlot {
  index: number;
  controller: SlotController;
  aiDifficulty: AiDifficulty;
  team: number;
  color: number;
  playerId: string | null;
  playerName: string | null;
  ready: boolean;
  connected: boolean;
}

export interface RoomInfo {
  code: string;
  hostId: string;
  settings: RoomSettings;
  slots: RoomSlot[];
  state: 'lobby' | 'running' | 'finished';
}

export interface RoomSummary {
  code: string;
  name: string;
  players: number;
  maxPlayers: number;
  state: RoomInfo['state'];
  mapSize: MapSize;
  scenario: ScenarioId | null;
  theatre: TheatreId | null;
}

export interface ChatLine {
  from: string;
  /** Slot color of the sender, or null for system messages. */
  color: number | null;
  text: string;
  time: number;
}

// ---- messages -------------------------------------------------------------------------------

export type ClientMessage =
  | { t: 'hello'; version: number; name: string; token?: string }
  | { t: 'listRooms' }
  | { t: 'createRoom'; settings: Partial<RoomSettings> }
  | { t: 'joinRoom'; code: string }
  | { t: 'leaveRoom' }
  | { t: 'updateSettings'; settings: Partial<RoomSettings> }
  | {
      t: 'setSlot';
      slot: number;
      controller?: SlotController;
      aiDifficulty?: AiDifficulty;
      team?: number;
      color?: number;
    }
  | { t: 'takeSlot'; slot: number }
  | { t: 'ready'; ready: boolean }
  | { t: 'startGame' }
  | { t: 'chat'; text: string }
  | { t: 'cmd'; seq: number; command: Command }
  | { t: 'setSpeed'; speed: GameSpeed }
  | { t: 'pause'; paused: boolean }
  /**
   * Observe the war — allowed only by the observer rule (engine `mayObserve`: our nation has
   * capitulated, in a free-for-all). `perspective` SPECTATOR = the whole truth, a faction id =
   * that staff's view. Sent again to switch perspective.
   */
  | { t: 'spectate'; perspective: FactionId }
  | { t: 'ping'; time: number };

/**
 * Machine-readable reason attached to `error` messages (additive; `message` stays the text shown
 * to the player). `version` means the client is outdated and must reload.
 */
export type ErrorCode =
  | 'version'
  | 'serverFull'
  | 'notFound'
  | 'roomFull'
  | 'forbidden'
  | 'state'
  | 'invalid'
  | 'rateLimited'
  | 'internal';

export type ServerMessage =
  | { t: 'welcome'; playerId: string; token: string; name: string }
  | { t: 'roomList'; rooms: RoomSummary[] }
  | { t: 'room'; room: RoomInfo | null }
  | { t: 'error'; message: string; code?: ErrorCode }
  | { t: 'chat'; line: ChatLine }
  | {
      t: 'gameStart';
      config: SessionConfig;
      map: GameMap;
      faction: FactionId;
      view: PlayerView;
      speed: GameSpeed;
      paused: boolean;
    }
  | { t: 'tick'; delta: ViewDelta }
  | { t: 'speed'; speed: GameSpeed; paused: boolean }
  | { t: 'cmdResult'; seq: number; result: CommandResult }
  | { t: 'pong'; time: number }
  /**
   * The war is over: the whole match (config + every applied command) for "The Fog Lifts"
   * replay. Sent once after the final tick, and again after `gameStart` to players who
   * (re)join the finished room. Never sent while the match runs — it lifts the fog.
   */
  | { t: 'replay'; log: ReplayLog }
  /**
   * Observing (answer to `spectate`, and after `gameStart` when an observer reconnects): a full
   * view from `perspective` — the whole truth when SPECTATOR — that replaces the current view and
   * the delta baseline; `tick` deltas follow. Only ever sent to players the observer rule admits.
   */
  | { t: 'spectate'; perspective: FactionId; view: PlayerView };
